import { useState } from 'react';
import { ExternalLink, Bell, CheckCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { userMessagesApi } from '../../utils/api';
import toast from 'react-hot-toast';

/**
 * Displays pending system messages one by one.
 * The user must confirm reading each message before proceeding.
 * The modal cannot be dismissed without confirming all messages.
 */
const UserMessagesModal = ({ messages, onAllRead }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  if (!messages || messages.length === 0) return null;

  const current = messages[currentIndex];
  const total = messages.length;
  const isLast = currentIndex === total - 1;

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await userMessagesApi.confirmRead(current.messageId);

      if (isLast) {
        setConfirmed(true);
        setTimeout(() => onAllRead(), 600);
      } else {
        setCurrentIndex(prev => prev + 1);
      }
    } catch (error) {
      toast.error('Failed to record confirmation. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  if (confirmed) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 flex flex-col items-center gap-4">
          <CheckCircle size={48} className="text-green-500" />
          <p className="text-gray-700 dark:text-gray-300 font-medium text-center">
            All messages confirmed!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full flex flex-col overflow-hidden">

        {/* Header */}
        <div className="bg-blue-600 dark:bg-blue-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bell size={20} className="text-white flex-shrink-0" />
            <span className="text-white font-semibold text-sm tracking-wide uppercase">
              System Message
            </span>
          </div>
          {/* Counter */}
          <span className="text-blue-200 text-sm font-mono font-medium">
            {currentIndex + 1}/{total}
          </span>
        </div>

        {/* Progress bar */}
        {total > 1 && (
          <div className="w-full h-1 bg-blue-100 dark:bg-blue-900/40">
            <div
              className="h-1 bg-blue-500 transition-all duration-300"
              style={{ width: `${((currentIndex + 1) / total) * 100}%` }}
            />
          </div>
        )}

        {/* Body */}
        <div className="px-6 py-6 flex flex-col gap-4">

          {/* Title */}
          <h2 className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
            {current.titulo}
          </h2>

          {/* Description — Markdown rendered */}
          <div className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {current.descricao}
            </ReactMarkdown>
          </div>

          {/* Optional URL */}
          {current.url && (
            <a
              href={current.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline transition-colors"
            >
              <ExternalLink size={14} />
              {current.url}
            </a>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex flex-col gap-3">
          {total > 1 && (
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
              {isLast
                ? 'This is the last message.'
                : `After confirming, you will see the next message (${currentIndex + 2}/${total}).`}
            </p>
          )}
          <button
            onClick={handleConfirm}
            disabled={confirming}
            className="w-full py-3 px-6 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {confirming ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle size={18} />
                {isLast ? 'Confirm reading' : 'Confirm and continue'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserMessagesModal;
